// src/frontend/src/pages/HomePage.tsx - Updated with new components
import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import HomeLayout from '../components/Layout/HomeLayout';
import HeroSection from '../components/Landing/HeroSection';
import LiveDemo from '../components/Landing/LiveDemo';
import StarterKits from '../components/Landing/StarterKits';
import ValueProposition from '../components/Landing/ValueProposition';
import PricingContent from '../components/Pricing/PricingContent';
import StickyFooterCTA from '../components/Common/StickyFooterCTA';

// Type definitions for Interactive Scenario Picker
interface Scenario {
  id: number;
  title: string;
  stakeholder: string;
  difficulty: string;
  preview: string;
  learningFocus: string;
}

interface ScenarioCategory {
  name: string;
  icon: string;
  color: string;
  scenarios: Scenario[];
}

type ScenarioCategoryKey = 'timeline' | 'budget' | 'scope';

// NEW: Interactive Scenario Picker Component
const InteractiveScenarioPicker: React.FC = () => {
  const [activeCategory, setActiveCategory] = useState<ScenarioCategoryKey>('timeline');
  const [selectedScenario, setSelectedScenario] = useState<Scenario | null>(null);

  const scenarioCategories: Record<ScenarioCategoryKey, ScenarioCategory> = {
    timeline: {
      name: 'Timeline Pressure',
      icon: '⏰',
      color: 'bg-red-50 border-red-200 text-red-800',
      scenarios: [
        {
          id: 1,
          title: 'Executive Demanding Impossible Timeline',
          stakeholder: 'VP of Product',
          difficulty: 'Hard',
          preview: '"I just heard from the board that we need this feature live by end of month. I don\'t care what it takes - make it happen."',
          learningFocus: 'Managing up under pressure while maintaining team welfare'
        },
        {
          id: 2,
          title: 'Client Wants to Move Up Deadline',
          stakeholder: 'External Client',
          difficulty: 'Medium',
          preview: '"Our marketing campaign is already scheduled. Can you deliver two weeks early?"',
          learningFocus: 'Balancing client needs with realistic delivery'
        }
      ]
    },
    budget: {
      name: 'Budget Constraints',
      icon: '💰',
      color: 'bg-yellow-50 border-yellow-200 text-yellow-800',
      scenarios: [
        {
          id: 3,
          title: 'CFO Cutting Project Budget in Half',
          stakeholder: 'Chief Financial Officer',
          difficulty: 'Hard',
          preview: '"We need to reduce all project budgets by 50%. Figure out how to deliver the same scope with half the resources."',
          learningFocus: 'Negotiating scope vs. budget trade-offs'
        },
        {
          id: 4,
          title: 'Team Lead Requesting More Resources',
          stakeholder: 'Technical Lead',
          difficulty: 'Medium',
          preview: '"This project is way more complex than we thought. We need at least 3 more developers or we\'re going to miss everything."',
          learningFocus: 'Managing resource requests and expectations'
        }
      ]
    },
    scope: {
      name: 'Scope Creep',
      icon: '📈',
      color: 'bg-blue-50 border-blue-200 text-blue-800',
      scenarios: [
        {
          id: 5,
          title: 'Marketing Wants Last-Minute Changes',
          stakeholder: 'Marketing Director',
          difficulty: 'Medium',
          preview: '"I know we\'re almost done, but the CEO just had this brilliant idea that would make this 10x better..."',
          learningFocus: 'Handling scope changes diplomatically'
        },
        {
          id: 6,
          title: 'Customer Success Escalating Feature Requests',
          stakeholder: 'Customer Success Manager',
          difficulty: 'Easy',
          preview: '"Our biggest client is threatening to leave unless we add this one small feature. It shouldn\'t take long, right?"',
          learningFocus: 'Managing stakeholder expectations and priorities'
        }
      ]
    }
  };

  const getDifficultyColor = (difficulty: string) => {
    switch (difficulty) {
      case 'Easy': return 'bg-green-100 text-green-800';
      case 'Medium': return 'bg-yellow-100 text-yellow-800';
      case 'Hard': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  return (
    <div className="py-16 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <h2 className="text-3xl font-extrabold text-gray-900 sm:text-4xl mb-4">
            Try Our Interactive Scenario Picker
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Experience how PM Coach adapts to your specific challenges. 
            Select a scenario type and see what real stakeholder conversations look like.
          </p>
        </div>

        <div className="max-w-6xl mx-auto">
          {/* Category Selector */}
          <div className="flex flex-wrap justify-center gap-4 mb-8">
            {(Object.entries(scenarioCategories) as [ScenarioCategoryKey, ScenarioCategory][]).map(([key, category]) => (
              <button
                key={key}
                onClick={() => {
                  setActiveCategory(key);
                  setSelectedScenario(null);
                }}
                className={`group relative px-6 py-4 rounded-xl font-medium transition-all duration-300 ${
                  activeCategory === key
                    ? 'bg-gradient-to-r from-blue-600 to-purple-600 text-white shadow-lg transform scale-105'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                <span className="text-2xl mr-2">{category.icon}</span>
                <span>{category.name}</span>
                {activeCategory === key && (
                  <div className="absolute -bottom-2 left-1/2 transform -translate-x-1/2 w-4 h-4 bg-blue-600 rotate-45"></div>
                )}
              </button>
            ))}
          </div>

          {/* Scenarios Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
            {scenarioCategories[activeCategory].scenarios.map((scenario: Scenario) => (
              <div
                key={scenario.id}
                className={`group cursor-pointer border-2 rounded-xl p-6 transition-all duration-300 hover:shadow-lg ${
                  selectedScenario?.id === scenario.id
                    ? 'border-blue-500 bg-blue-50'
                    : 'border-gray-200 bg-white hover:border-gray-300'
                }`}
                onClick={() => setSelectedScenario(scenario)}
              >
                <div className="flex justify-between items-start mb-4">
                  <h3 className="text-lg font-semibold text-gray-900 group-hover:text-blue-700">
                    {scenario.title}
                  </h3>
                  <span className={`px-3 py-1 text-xs font-medium rounded-full ${getDifficultyColor(scenario.difficulty)}`}>
                    {scenario.difficulty}
                  </span>
                </div>

                <div className="mb-4">
                  <p className="text-sm text-gray-600 mb-2">
                    <strong>Stakeholder:</strong> {scenario.stakeholder}
                  </p>
                  <div className="bg-gray-50 border-l-4 border-gray-300 p-3 rounded-r-lg">
                    <p className="text-sm italic text-gray-700">
                      "{scenario.preview}"
                    </p>
                  </div>
                </div>

                <div className="pt-3 border-t border-gray-200">
                  <p className="text-xs text-gray-600">
                    <strong>You'll learn:</strong> {scenario.learningFocus}
                  </p>
                </div>

                {selectedScenario?.id === scenario.id && (
                  <div className="mt-4 pt-4 border-t border-blue-200">
                    <Link
                      to="/register"
                      className="block w-full text-center bg-blue-600 text-white py-2 px-4 rounded-lg hover:bg-blue-700 transition-colors"
                    >
                      Start This Scenario
                    </Link>
                  </div>
                )}
              </div>
            ))}
          </div>

          {/* Selected Scenario Details */}
          {selectedScenario && (
            <div className="bg-gradient-to-br from-blue-50 to-indigo-50 border border-blue-200 rounded-xl p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-xl font-semibold text-blue-900">
                  Ready to practice: {selectedScenario.title}
                </h3>
                <span className={`px-3 py-1 text-sm font-medium rounded-full ${getDifficultyColor(selectedScenario.difficulty)}`}>
                  {selectedScenario.difficulty} Level
                </span>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
                <div className="text-center">
                  <div className="w-12 h-12 bg-blue-500 rounded-full flex items-center justify-center mx-auto mb-2">
                    <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                    </svg>
                  </div>
                  <p className="text-sm font-medium text-blue-800">Dynamic Conversation</p>
                  <p className="text-xs text-blue-600">AI adapts to your responses</p>
                </div>
                <div className="text-center">
                  <div className="w-12 h-12 bg-green-500 rounded-full flex items-center justify-center mx-auto mb-2">
                    <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                    </svg>
                  </div>
                  <p className="text-sm font-medium text-green-800">Real-time Coaching</p>
                  <p className="text-xs text-green-600">Get tips as you type</p>
                </div>
                <div className="text-center">
                  <div className="w-12 h-12 bg-purple-500 rounded-full flex items-center justify-center mx-auto mb-2">
                    <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v4a2 2 0 01-2 2h-2a2 2 0 00-2-2z" />
                    </svg>
                  </div>
                  <p className="text-sm font-medium text-purple-800">Detailed Feedback</p>
                  <p className="text-xs text-purple-600">Actionable improvement tips</p>
                </div>
              </div>

              <div className="text-center">
                <Link
                  to="/register"
                  className="inline-flex items-center px-8 py-4 bg-gradient-to-r from-blue-600 to-purple-600 text-white font-medium rounded-lg hover:from-blue-700 hover:to-purple-700 transition-all duration-200 shadow-lg hover:shadow-xl transform hover:scale-105"
                >
                  <span>Start Free Trial to Practice This Scenario</span>
                  <svg className="ml-2 w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                  </svg>
                </Link>
                <p className="text-sm text-blue-600 mt-2">
                  No credit card required • 20 coaching sessions free
                </p>
              </div>
            </div>
          )}

          {/* Call to Action */}
          {!selectedScenario && (
            <div className="text-center bg-gray-50 rounded-xl p-8">
              <h3 className="text-xl font-semibold text-gray-900 mb-2">
                👆 Select any scenario above to see how PM Coach works
              </h3>
              <p className="text-gray-600 mb-6">
                These are just 6 of our 50+ realistic scenarios. Each one adapts to your responses 
                and provides personalized coaching feedback.
              </p>
              <Link
                to="/register"
                className="inline-flex items-center px-6 py-3 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 transition-colors"
              >
                Browse All Scenarios
                <svg className="ml-2 w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" />
                </svg>
              </Link>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// Type definitions for Enhanced Comparison Table
interface CompetitorFeatures {
  pmSpecific: boolean;
  practiceConversations: boolean;
  realTimeCoaching: boolean;
  immediateAccess: boolean;
  scenarioLibrary: boolean;
  stakeholderFocus: boolean;
  costEffective: boolean;
  measurableProgress: boolean;
}

interface Competitor {
  name: string;
  logo: string;
  pricing: string;
  focus: string;
  features: CompetitorFeatures;
}

interface ComparisonData {
  title: string;
  subtitle: string;
  competitors: Competitor[];
}

type ComparisonKey = 'coaching' | 'training' | 'tools';

// NEW: Enhanced Comparison Table Component
const EnhancedComparisonTable: React.FC = () => {
  const [activeComparison, setActiveComparison] = useState<ComparisonKey>('coaching');

  const comparisonData: Record<ComparisonKey, ComparisonData> = {
    coaching: {
      title: 'PM Coach vs. Generic Executive Coaching',
      subtitle: 'Why PM-specific training beats general leadership advice',
      competitors: [
        {
          name: 'BetterUp',
          logo: '🎯',
          pricing: '$300-500/month',
          focus: 'General leadership development',
          features: {
            pmSpecific: false,
            practiceConversations: false,
            realTimeCoaching: false,
            immediateAccess: true,
            scenarioLibrary: false,
            stakeholderFocus: false,
            costEffective: false,
            measurableProgress: true
          }
        },
        {
          name: 'CoachHub',
          logo: '💼',
          pricing: '$400-600/month',
          focus: 'Executive coaching for leaders',
          features: {
            pmSpecific: false,
            practiceConversations: false,
            realTimeCoaching: false,
            immediateAccess: false,
            scenarioLibrary: false,
            stakeholderFocus: false,
            costEffective: false,
            measurableProgress: true
          }
        },
        {
          name: 'PM Coach',
          logo: '🚀',
          pricing: '$19-39/month',
          focus: 'PM stakeholder conversations',
          features: {
            pmSpecific: true,
            practiceConversations: true,
            realTimeCoaching: true,
            immediateAccess: true,
            scenarioLibrary: true,
            stakeholderFocus: true,
            costEffective: true,
            measurableProgress: true
          }
        }
      ]
    },
    training: {
      title: 'PM Coach vs. Traditional PM Training',
      subtitle: 'Practice beats theory when it comes to stakeholder conversations',
      competitors: [
        {
          name: 'PMP Certification',
          logo: '📚',
          pricing: '$3,000-5,000',
          focus: 'Theoretical frameworks & processes',
          features: {
            pmSpecific: true,
            practiceConversations: false,
            realTimeCoaching: false,
            immediateAccess: false,
            scenarioLibrary: false,
            stakeholderFocus: false,
            costEffective: false,
            measurableProgress: false
          }
        },
        {
          name: 'Corporate PM Training',
          logo: '🏢',
          pricing: '$2,000-8,000',
          focus: 'Company-specific processes',
          features: {
            pmSpecific: true,
            practiceConversations: false,
            realTimeCoaching: false,
            immediateAccess: false,
            scenarioLibrary: false,
            stakeholderFocus: false,
            costEffective: false,
            measurableProgress: false
          }
        },
        {
          name: 'PM Coach',
          logo: '🚀',
          pricing: '$19-39/month',
          focus: 'Practical conversation skills',
          features: {
            pmSpecific: true,
            practiceConversations: true,
            realTimeCoaching: true,
            immediateAccess: true,
            scenarioLibrary: true,
            stakeholderFocus: true,
            costEffective: true,
            measurableProgress: true
          }
        }
      ]
    },
    tools: {
      title: 'PM Coach vs. PM Software Tools',
      subtitle: 'Soft skills matter more than Gantt charts for PM success',
      competitors: [
        {
          name: 'Asana/Monday',
          logo: '📊',
          pricing: '$10-25/user/month',
          focus: 'Task & project management',
          features: {
            pmSpecific: true,
            practiceConversations: false,
            realTimeCoaching: false,
            immediateAccess: true,
            scenarioLibrary: false,
            stakeholderFocus: false,
            costEffective: true,
            measurableProgress: false
          }
        },
        {
          name: 'Jira',
          logo: '🛠️',
          pricing: '$7-14/user/month',
          focus: 'Technical project tracking',
          features: {
            pmSpecific: true,
            practiceConversations: false,
            realTimeCoaching: false,
            immediateAccess: true,
            scenarioLibrary: false,
            stakeholderFocus: false,
            costEffective: true,
            measurableProgress: false
          }
        },
        {
          name: 'PM Coach',
          logo: '🚀',
          pricing: '$19-39/month',
          focus: 'PM communication skills',
          features: {
            pmSpecific: true,
            practiceConversations: true,
            realTimeCoaching: true,
            immediateAccess: true,
            scenarioLibrary: true,
            stakeholderFocus: true,
            costEffective: true,
            measurableProgress: true
          }
        }
      ]
    }
  };

  const featureLabels: Record<keyof CompetitorFeatures, string> = {
    pmSpecific: 'PM-Specific Content',
    practiceConversations: 'Practice Real Conversations',
    realTimeCoaching: 'Real-Time Coaching Tips',
    immediateAccess: 'Available 24/7',
    scenarioLibrary: '50+ Realistic Scenarios',
    stakeholderFocus: 'Stakeholder Management Focus',
    costEffective: 'Affordable for All PMs',
    measurableProgress: 'Track Progress & Improvement'
  };

  const currentComparison = comparisonData[activeComparison];

  return (
    <div className="py-16 bg-gradient-to-br from-gray-50 to-blue-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <h2 className="text-3xl font-extrabold text-gray-900 sm:text-4xl mb-4">
            How PM Coach Compares
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Unlike generic solutions, PM Coach is built specifically for project managers 
            who need to master stakeholder conversations.
          </p>
        </div>

        {/* Comparison Category Selector */}
        <div className="flex flex-wrap justify-center gap-4 mb-12">
          {(Object.entries(comparisonData) as [ComparisonKey, ComparisonData][]).map(([key, data]) => (
            <button
              key={key}
              onClick={() => setActiveComparison(key)}
              className={`px-6 py-3 rounded-lg font-medium transition-all duration-300 ${
                activeComparison === key
                  ? 'bg-blue-600 text-white shadow-lg'
                  : 'bg-white text-gray-700 border border-gray-200 hover:border-blue-300'
              }`}
            >
              {data.title.split(' vs. ')[1]}
            </button>
          ))}
        </div>

        {/* Comparison Table */}
        <div className="bg-white rounded-2xl shadow-xl overflow-hidden">
          <div className="bg-gradient-to-r from-blue-600 to-purple-600 px-6 py-8 text-white text-center">
            <h3 className="text-2xl font-bold mb-2">{currentComparison.title}</h3>
            <p className="text-blue-100">{currentComparison.subtitle}</p>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full">
              {/* Header */}
              <thead>
                <tr className="bg-gray-50">
                  <th className="px-6 py-4 text-left text-sm font-medium text-gray-500 uppercase tracking-wider">
                    Solution
                  </th>
                  {currentComparison.competitors.map((competitor: Competitor, index: number) => (
                    <th key={index} className={`px-6 py-4 text-center text-sm font-medium uppercase tracking-wider ${
                      competitor.name === 'PM Coach' 
                        ? 'bg-blue-50 text-blue-800' 
                        : 'text-gray-500'
                    }`}>
                      <div className="flex flex-col items-center">
                        <span className="text-2xl mb-2">{competitor.logo}</span>
                        <span className="font-semibold">{competitor.name}</span>
                        <span className="text-xs font-normal mt-1 text-gray-600">
                          {competitor.pricing}
                        </span>
                      </div>
                    </th>
                  ))}
                </tr>
              </thead>

              {/* Focus Row */}
              <tbody>
                <tr className="border-b border-gray-200">
                  <td className="px-6 py-4 text-sm font-medium text-gray-900 bg-gray-50">
                    Primary Focus
                  </td>
                  {currentComparison.competitors.map((competitor: Competitor, index: number) => (
                    <td key={index} className={`px-6 py-4 text-center text-sm ${
                      competitor.name === 'PM Coach' ? 'bg-blue-50' : ''
                    }`}>
                      <span className={`font-medium ${
                        competitor.name === 'PM Coach' ? 'text-blue-800' : 'text-gray-700'
                      }`}>
                        {competitor.focus}
                      </span>
                    </td>
                  ))}
                </tr>

                {/* Feature Rows */}
                {Object.entries(featureLabels).map(([featureKey, label]) => (
                  <tr key={featureKey} className="border-b border-gray-200 hover:bg-gray-50">
                    <td className="px-6 py-4 text-sm font-medium text-gray-900 bg-gray-50">
                      {label}
                    </td>
                    {currentComparison.competitors.map((competitor: Competitor, index: number) => (
                      <td key={index} className={`px-6 py-4 text-center ${
                        competitor.name === 'PM Coach' ? 'bg-blue-50' : ''
                      }`}>
                        {competitor.features[featureKey as keyof CompetitorFeatures] ? (
                          <div className="flex justify-center">
                            <svg className={`w-6 h-6 ${
                              competitor.name === 'PM Coach' ? 'text-blue-600' : 'text-green-500'
                            }`} fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                            </svg>
                          </div>
                        ) : (
                          <div className="flex justify-center">
                            <svg className="w-6 h-6 text-gray-300" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                            </svg>
                          </div>
                        )}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Bottom CTA */}
          <div className="bg-gradient-to-r from-blue-600 to-purple-600 px-6 py-8 text-center">
            <h3 className="text-xl font-semibold text-white mb-4">
              Ready to Experience the PM Coach Difference?
            </h3>
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
              <Link
                to="/register"
                className="inline-flex items-center px-8 py-3 bg-white text-blue-600 font-medium rounded-lg hover:bg-gray-50 transition-colors shadow-lg"
              >
                Start Free Trial
                <svg className="ml-2 w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                </svg>
              </Link>
              <p className="text-blue-100 text-sm">
                20 coaching sessions free • No credit card required
              </p>
            </div>
          </div>
        </div>

        {/* Key Insights */}
        <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="bg-white rounded-lg p-6 shadow-md border-l-4 border-blue-500">
            <h4 className="font-semibold text-gray-900 mb-2">🎯 PM-Specific</h4>
            <p className="text-sm text-gray-600">
              While others focus on general leadership or project tools, we're laser-focused on the conversations that make or break PM careers.
            </p>
          </div>
          <div className="bg-white rounded-lg p-6 shadow-md border-l-4 border-green-500">
            <h4 className="font-semibold text-gray-900 mb-2">💰 10x More Affordable</h4>
            <p className="text-sm text-gray-600">
              Get better results than $500/month executive coaching for less than the cost of a Netflix subscription.
            </p>
          </div>
          <div className="bg-white rounded-lg p-6 shadow-md border-l-4 border-purple-500">
            <h4 className="font-semibold text-gray-900 mb-2">⚡ Immediate Impact</h4>
            <p className="text-sm text-gray-600">
              Start practicing today and see improvement in hours, not months. No waiting for coach availability or expensive training sessions.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

// NEW: Real-time Coaching Preview Component
const RealTimeCoachingPreview: React.FC = () => {
  return (
    <div className="py-16 bg-gradient-to-br from-blue-50 to-indigo-100">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <h2 className="text-3xl font-extrabold text-gray-900 sm:text-4xl mb-4">
            Real-Time AI Coaching While You Practice
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Unlike other platforms that only give feedback after the fact, PM Coach provides 
            intelligent tips <strong>as you type</strong> to improve your responses in real-time.
          </p>
        </div>

        <div className="max-w-4xl mx-auto">
          {/* Coaching Tip Preview */}
          <div className="bg-white rounded-lg shadow-xl overflow-hidden">
            <div className="bg-blue-600 text-white px-6 py-4">
              <h3 className="text-lg font-medium">Live Coaching Session</h3>
              <p className="text-blue-100 text-sm">Scenario: Executive asking about project delays</p>
            </div>
            
            <div className="p-6">
              {/* Mock conversation */}
              <div className="mb-4">
                <div className="flex justify-start mb-3">
                  <div className="max-w-xs lg:max-w-md px-4 py-2 rounded-lg bg-gray-200 text-gray-800">
                    <p className="text-sm">"I just heard from marketing that we're behind schedule. What's going on with this project?"</p>
                  </div>
                </div>
              </div>

              {/* User typing area with coaching tip */}
              <div className="border-t pt-4">
                <div className="mb-3">
                  <input
                    type="text"
                    value="Sorry, we ran into some technical issues..."
                    className="w-full p-3 border rounded-lg bg-gray-50"
                    disabled
                  />
                </div>
                
                {/* Real-time coaching tip */}
                <div className="bg-yellow-50 border border-yellow-200 p-4 rounded-lg">
                  <div className="flex items-start">
                    <div className="w-8 h-8 bg-yellow-500 rounded-full flex items-center justify-center mr-3 flex-shrink-0">
                      <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                      </svg>
                    </div>
                    <div>
                      <h4 className="font-medium text-yellow-800">💡 Real-time Coaching Tip</h4>
                      <p className="text-yellow-700 text-sm mt-1">
                        Instead of starting with an apology, try acknowledging their concern first. 
                        Consider: "I understand your concern about the timeline. Let me give you a clear update on where we stand..."
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Benefits of real-time coaching */}
          <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="text-center">
              <div className="w-12 h-12 bg-blue-500 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                </svg>
              </div>
              <h3 className="font-semibold text-gray-900 mb-2">Learn as You Practice</h3>
              <p className="text-gray-600 text-sm">Get immediate guidance to improve your approach before sending your response</p>
            </div>
            <div className="text-center">
              <div className="w-12 h-12 bg-green-500 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <h3 className="font-semibold text-gray-900 mb-2">Build Better Habits</h3>
              <p className="text-gray-600 text-sm">Develop stronger communication patterns through guided practice</p>
            </div>
            <div className="text-center">
              <div className="w-12 h-12 bg-purple-500 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
                </svg>
              </div>
              <h3 className="font-semibold text-gray-900 mb-2">Faster Improvement</h3>
              <p className="text-gray-600 text-sm">See results in hours, not months of trial-and-error</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// NEW: Sample Feedback Report Preview Component
const FeedbackReportPreview: React.FC = () => {
  return (
    <div className="py-16 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <h2 className="text-3xl font-extrabold text-gray-900 sm:text-4xl mb-4">
            Detailed Performance Analytics You Can Actually Use
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Every coaching session ends with a comprehensive feedback report 
            that identifies exactly what to improve and how to do it.
          </p>
        </div>

        <div className="max-w-4xl mx-auto">
          {/* Sample feedback report preview */}
          <div className="bg-white rounded-lg shadow-xl border border-gray-200 overflow-hidden">
            <div className="bg-gradient-to-r from-green-500 to-emerald-600 text-white px-6 py-4">
              <h3 className="text-lg font-medium">Your Coaching Report</h3>
              <p className="text-green-100 text-sm">Session: Timeline Pressure from Executive Sponsor</p>
            </div>

            <div className="p-6">
              {/* Overall Score */}
              <div className="text-center mb-8">
                <div className="inline-flex items-center justify-center w-20 h-20 bg-green-100 rounded-full mb-4">
                  <span className="text-3xl font-bold text-green-600">8.5</span>
                </div>
                <p className="text-gray-600">Overall Performance Score</p>
                <p className="text-sm text-gray-500 mt-1">Above average! You handled this well.</p>
              </div>

              {/* Key insights */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
                <div className="bg-green-50 p-4 rounded-lg border border-green-200">
                  <h4 className="font-semibold text-green-800 mb-3 flex items-center">
                    <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                    </svg>
                    What You Did Well
                  </h4>
                  <ul className="text-sm text-gray-700 space-y-1">
                    <li>• Acknowledged stakeholder concerns immediately</li>
                    <li>• Provided specific timeline alternatives</li>
                    <li>• Maintained professional tone under pressure</li>
                  </ul>
                </div>
                <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                  <h4 className="font-semibold text-blue-800 mb-3 flex items-center">
                    <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                    </svg>
                    Areas to Improve
                  </h4>
                  <ul className="text-sm text-gray-700 space-y-1">
                    <li>• Could have quantified business impact sooner</li>
                    <li>• Opportunity to involve stakeholder in solution</li>
                    <li>• Consider addressing root causes</li>
                  </ul>
                </div>
              </div>

              {/* Specific actionable feedback */}
              <div className="bg-yellow-50 border border-yellow-200 p-4 rounded-lg">
                <h4 className="font-semibold text-yellow-800 mb-2">💡 Next Time, Try This:</h4>
                <p className="text-yellow-700 text-sm">
                  "When facing timeline pressure, lead with business impact: 'I understand this affects our Q4 launch. 
                  Here's what we can deliver by the original date vs. what we'd gain with 2 more weeks.' 
                  This frames the conversation around business value rather than just project logistics."
                </p>
              </div>

              {/* Export CTA */}
              <div className="mt-6 text-center">
                <button className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors">
                  <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                  Export Full Report (PDF)
                </button>
                <p className="text-xs text-gray-500 mt-2">Share with your manager or keep for performance reviews</p>
              </div>
            </div>
          </div>

          {/* Comparison with other solutions */}
          <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="text-center p-6 bg-gray-50 rounded-lg">
              <h4 className="font-semibold text-gray-900 mb-2">Generic Leadership Coaching</h4>
              <p className="text-sm text-gray-600 mb-4">"Work on your communication skills"</p>
              <span className="inline-flex items-center px-3 py-1 rounded-full text-xs bg-red-100 text-red-800">
                Vague advice
              </span>
            </div>
            <div className="text-center p-6 bg-gray-50 rounded-lg">
              <h4 className="font-semibold text-gray-900 mb-2">Traditional PM Training</h4>
              <p className="text-sm text-gray-600 mb-4">"Review PMBOK stakeholder management chapter"</p>
              <span className="inline-flex items-center px-3 py-1 rounded-full text-xs bg-yellow-100 text-yellow-800">
                Theory only
              </span>
            </div>
            <div className="text-center p-6 bg-blue-50 rounded-lg border-2 border-blue-200">
              <h4 className="font-semibold text-blue-900 mb-2">PM Coach</h4>
              <p className="text-sm text-blue-700 mb-4">"Next time, lead with business impact. Here's the exact phrase to use..."</p>
              <span className="inline-flex items-center px-3 py-1 rounded-full text-xs bg-blue-100 text-blue-800">
                Actionable & specific
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const HomePage: React.FC = () => {
  return (
    <HomeLayout>
      {/* Early Hook - Build Interest */}
      <HeroSection />
      <LiveDemo />
      
      {/* NEW: Interactive Scenario Picker - Moved up for early engagement */}
      <InteractiveScenarioPicker />
      
      {/* Value Building - Create Desire */}
      <RealTimeCoachingPreview />
      <FeedbackReportPreview />
      <StarterKits />
      
      {/* Objection Handling - Remove Friction */}
      <EnhancedComparisonTable />
      
      {/* Social Proof - Build Trust Before Pricing */}
      {/* Social Proof - Build Trust Before Pricing */}
      <div className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-extrabold text-gray-900 text-center mb-4">
            Real Results from Real Project Managers
          </h2>
          <p className="text-xl text-gray-600 text-center mb-12 max-w-3xl mx-auto">
            Unlike generic coaching that takes months to show results, PM Coach delivers 
            measurable improvements in hours of practice.
          </p>
          
          {/* Enhanced metrics */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8 mb-12">
            <div className="text-center">
              <div className="text-4xl font-bold text-blue-600 mb-2">28%</div>
              <div className="text-sm text-gray-600">Improvement in stakeholder satisfaction scores</div>
              <div className="text-xs text-gray-500 mt-1">Fortune 500 Tech Company</div>
            </div>
            <div className="text-center">
              <div className="text-4xl font-bold text-green-600 mb-2">2.3</div>
              <div className="text-sm text-gray-600">Average score improvement (10-point scale)</div>
              <div className="text-xs text-gray-500 mt-1">After 10 hours of practice</div>
            </div>
            <div className="text-center">
              <div className="text-4xl font-bold text-purple-600 mb-2">85%</div>
              <div className="text-sm text-gray-600">Report improved confidence in difficult conversations</div>
              <div className="text-xs text-gray-500 mt-1">After 14-day trial</div>
            </div>
            <div className="text-center">
              <div className="text-4xl font-bold text-orange-600 mb-2">47%</div>
              <div className="text-sm text-gray-600">Higher interview success rate for promotions</div>
              <div className="text-xs text-gray-500 mt-1">Among Solo Pro users</div>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="bg-white p-6 rounded-lg shadow">
              <div className="flex mb-4">
                {[...Array(5)].map((_, i) => (
                  <svg key={i} className="w-5 h-5 text-yellow-400" fill="currentColor" viewBox="0 0 20 20">
                    <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                  </svg>
                ))}
              </div>
              <p className="text-gray-600 italic mb-4">
                "I went from freezing up in executive meetings to confidently handling pushback. 
                The real-time coaching tips helped me reframe responses before I said something I'd regret. 
                <strong>Got promoted to Senior PM within 6 months.</strong>"
              </p>
              <p className="font-medium">Sarah Chen</p>
              <p className="text-sm text-gray-500">Senior PM at TechCorp • Score improved from 6.2 to 8.7</p>
            </div>
            
            <div className="bg-white p-6 rounded-lg shadow">
              <div className="flex mb-4">
                {[...Array(5)].map((_, i) => (
                  <svg key={i} className="w-5 h-5 text-yellow-400" fill="currentColor" viewBox="0 0 20 20">
                    <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                  </svg>
                ))}
              </div>
              <p className="text-gray-600 italic mb-4">
                "Our PMO tried BetterUp for general coaching but it didn't address PM-specific challenges. 
                PM Coach gave our team the targeted practice they needed. 
                <strong>Our stakeholder satisfaction scores jumped 28% in 3 months.</strong>"
              </p>
              <p className="font-medium">Michael Rodriguez</p>
              <p className="text-sm text-gray-500">PMO Director at Global Finance • 15-person team</p>
            </div>
            
            <div className="bg-white p-6 rounded-lg shadow">
              <div className="flex mb-4">
                {[...Array(5)].map((_, i) => (
                  <svg key={i} className="w-5 h-5 text-yellow-400" fill="currentColor" viewBox="0 0 20 20">
                    <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                  </svg>
                ))}
              </div>
              <p className="text-gray-600 italic mb-4">
                "Worth 10x more than my PMP certification. Instead of memorizing theory, 
                I'm actually practicing the conversations that matter daily. 
                <strong>Finally feel confident when stakeholders push back on timelines.</strong>"
              </p>
              <p className="font-medium">Priya Patel</p>
              <p className="text-sm text-gray-500">PM at Healthcare Innovations • 3 years experience</p>
            </div>
          </div>
        </div>
      </div>

      {/* Conversion - Drive Action */}
      <PricingContent 
        showFullFAQ={false} 
        showBillingToggle={true}
        sectionId="pricing"
      />

      {/* Enhanced Final CTA with stronger trial messaging */}
      <div className="bg-blue-600 py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-extrabold text-white mb-4">
            Ready to Master PM Stakeholder Conversations?
          </h2>
          <p className="text-xl text-blue-100 mb-8 max-w-2xl mx-auto">
            Join thousands of PMs who chose focused practice over generic advice. 
            Get <strong>20 coaching sessions free</strong> – no credit card required.
          </p>
          
          {/* Enhanced trial value messaging */}
          <div className="bg-blue-500/30 rounded-lg p-6 mb-8 max-w-3xl mx-auto">
            <h3 className="text-lg font-semibold text-white mb-4">Your Free Trial Includes:</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-left">
              <div className="flex items-center text-blue-100">
                <svg className="w-5 h-5 mr-3 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
                20 PM-specific coaching sessions
              </div>
              <div className="flex items-center text-blue-100">
                <svg className="w-5 h-5 mr-3 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
                Real-time AI coaching tips
              </div>
              <div className="flex items-center text-blue-100">
                <svg className="w-5 h-5 mr-3 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
                Detailed PDF feedback reports
              </div>
              <div className="flex items-center text-blue-100">
                <svg className="w-5 h-5 mr-3 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
                50+ realistic scenarios
              </div>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/register"
              className="inline-flex items-center px-8 py-4 border border-transparent text-lg font-medium rounded-md text-blue-600 bg-white hover:bg-gray-50 transition duration-150"
            >
              Get 20 Free Coaching Sessions
              <svg className="ml-2 w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
              </svg>
            </Link>
            <Link
              to="/contact"
              className="inline-flex items-center px-8 py-4 border-2 border-white text-lg font-medium rounded-md text-white hover:bg-white hover:text-blue-600 transition duration-150"
            >
              Schedule a Team Demo
            </Link>
          </div>
          <p className="mt-4 text-blue-100">
            <strong>No credit card required</strong> • Cancel anytime • See results in hours, not months
          </p>
        </div>
      </div>
      
      <StickyFooterCTA />
    </HomeLayout>
  );
};

export default HomePage;